import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupCount, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DataTable, Icon, KpiCard, KpiGrid, TextoTruncado, Tip } from "@/components/ds";
import { CeldaDocumentosRecurso } from "@/components/recurso-documentos/CeldaDocumentosRecurso";
import {
  FormDrawer,
  FormDrawerContent,
  FormDrawerDescription,
  FormDrawerFooter,
  FormDrawerFooterHint,
  FormDrawerHeader,
  FormDrawerTitle,
  FormDrawerClose,
  formDrawerBodyClass,
} from "@/components/ds/FormDrawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertCircle, Loader2, Plus, Search } from "@/components/ds/icons";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { formatNombreArea, ordenarAreas } from "@/lib/area";
import { estadoDocumental } from "@/lib/recurso-documentos-estado";
import { MaquinariaForm } from "@/pages/recursos/MaquinariaForm";
import type { Maquinaria as MaquinariaType, MaquinariasResponse } from "@/types/maquinaria";
import { PageHeader } from "@/components/ui/PageHeader";
import type { DriveStep } from "driver.js";
import { useGuide } from "@/hooks/useGuide";
import { useRegisterPageGuide } from "@/contexts/ActiveGuideContext";
import { useGuideAutostart } from "@/hooks/useGuideAutostart";
import { usePendingGuide } from "@/hooks/usePendingGuide";
import { useSetupStatusContext } from "@/contexts/SetupStatusContext";

// ─── Vocabulario ─────────────────────────────────────────────────────────────

/**
 * Las cuatro vistas del listado. `papelera` NO es un estado más de la
 * maquinaria: es un conjunto aparte que se le pide al servidor con otro filtro,
 * por eso viaja como vista y no como un valor del filtro de estado.
 *
 * `todos` es activas + inactivas, el mismo padrón sobre el que se calculan las
 * tarjetas — así el número del riel y el de la tarjeta "Total en inventario"
 * no pueden discrepar.
 */
type Vista = "todos" | "activos" | "inactivos" | "papelera";

function plural(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

// El criterio documental (qué es "al día", qué tono lleva la nota) vive en
// `lib/recurso-documentos-estado` y la celda en `CeldaDocumentosRecurso`,
// compartidos con Vehículos: las tres pantallas de equipos tienen el mismo
// diseño y el mismo contrato de API, así que un criterio por pantalla es
// exactamente cómo dos de ellas terminan midiendo distinto.

// ─── Celdas ──────────────────────────────────────────────────────────────────

/**
 * El área acompaña al nombre en vez de tener columna propia, como en el
 * prototipo: es un clasificador OPCIONAL, y una columna vacía para las empresas
 * que no usan áreas se come el ancho que necesita Documentos.
 */
function CeldaNombre({ m }: { m: MaquinariaType }) {
  const areas = ordenarAreas(m.areas ?? []);

  return (
    <div className="flex min-w-0 flex-col gap-0.5 pr-3">
      <TextoTruncado className="fc-cell block truncate">{m.nombre}</TextoTruncado>
      <TextoTruncado className="text-soft block truncate text-[11px]">
        {areas.length === 0 ? "Sin área" : areas.map(formatNombreArea).join(" · ")}
      </TextoTruncado>
    </div>
  );
}

function EstadoBadge({ m }: { m: MaquinariaType }) {
  if (m.eliminado_at) {
    return (
      <Badge variant="muted" dot>
        En papelera
      </Badge>
    );
  }
  return (
    <Badge variant={m.activo ? "success" : "neutral"} dot>
      {m.activo ? "Activa" : "Inactiva"}
    </Badge>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function Maquinaria() {
  const [, navigate] = useLocation();

  /**
   * Todo lo que NO está en la papelera: activas + inactivas. Es el conjunto
   * sobre el que se calculan las cuatro tarjetas, para que numerador y
   * denominador salgan siempre del mismo padrón.
   */
  const [inventario, setInventario] = useState<MaquinariaType[]>([]);
  const [papelera, setPapelera] = useState<MaquinariaType[]>([]);
  const [tiposTotal, setTiposTotal] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>("activos");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  // Mover a papelera (DELETE → soft delete)
  const [aEliminar, setAEliminar] = useState<MaquinariaType | null>(null);
  const [eliminando, setEliminando] = useState(false);

  // Restaurar desde papelera
  const [aRestaurar, setARestaurar] = useState<MaquinariaType | null>(null);
  const [restaurando, setRestaurando] = useState(false);

  // Purgar (hard delete)
  const [aPurgar, setAPurgar] = useState<MaquinariaType | null>(null);
  const [purgando, setPurgando] = useState(false);
  const [confirmPurgarTexto, setConfirmPurgarTexto] = useState("");

  // --- Guía de Maquinaria ---
  const { invalidate } = useSetupStatusContext();

  /**
   * guideInDialog: true mientras la guía está activa con el diálogo abierto.
   * Se usa para pasar modal={false} al FormDrawer durante esos pasos,
   * evitando que el focus trap de Radix bloquee los botones Siguiente/Anterior
   * del popover de driver.js (que se renderizan fuera del DOM del diálogo).
   *
   * En el flujo normal (sin guía) el diálogo sigue siendo modal={true} por defecto.
   */
  const [guideInDialog, setGuideInDialog] = useState(false);

  /**
   * Ref para cancelar el rAF de polling del primer campo del diálogo.
   * Se necesita para no tener efectos secundarios si el componente desmonta.
   */
  const dialogPollRafRef = useRef<number>(0);

  /**
   * Fábrica de pasos lazy: se invoca en startGuide() para capturar el estado
   * vivo de setCreateOpen y setGuideInDialog (estables entre renders).
   *
   * Paso 2 — Highlight del botón Nueva maquinaria:
   *   - onHighlightStarted: agrega driver-fc-dialog-step al body → driver z-index
   *     baja a 40/45 (bajo Radix z-50), el botón queda resaltado sin tapar nada.
   *   - onDeselected: remueve driver-fc-dialog-step → driver vuelve a su z default (~10000).
   *   - onNextClick: abre el diálogo, activa modal={false} en el dialog, hace poll
   *     rAF para esperar que monte el primer campo antes de avanzar.
   *
   * Pasos 3–5 — Campos dentro del diálogo, en el mismo orden en que se ven
   * (tipo → nombre/código → marca/modelo), para que el foco no salte hacia
   * atrás:
   *   - driver en z default (~10000), por encima del Radix Dialog (z-50).
   *   - driver-fc-dialog-step NO está en el body → overlay de driver es visible.
   *   - Radix Dialog tiene modal={false} → focus trap desactivado →
   *     botones Next/Prev de driver son clickeables.
   *
   * Paso 6 — Botón Guardar (dentro del diálogo):
   *   - onNextClick: desactiva modal={false}, avanza al popover de cierre.
   *
   * Paso 7 — Popover de cierre:
   *   - Desactiva modal={false}, el diálogo puede cerrarse normalmente.
   */
  const buildMaquinariaGuideSteps = useCallback((): DriveStep[] => [
    // Paso 1 — Popover centrado de apertura (sin elemento anclado)
    {
      popover: {
        title: "¿Para qué registrar maquinaria?",
        description:
          "Acá registras la maquinaria pesada y los equipos específicos de obra de tu empresa — por ejemplo, excavadoras, grúas o compactadores. Sin maquinaria registrada no podrás realizar los checks de maquinaria ni llevar el control de tus equipos. Es un paso opcional, pero necesario si operas con este tipo de activos. Creemos el primero.",
      },
    },
    // Paso 2 — Highlight del botón Nueva maquinaria (pre-diálogo)
    // driver-fc-dialog-step body class está activa → driver z-index bajo (40/45) bajo Radix
    {
      element: '[data-tour="maquinaria-crear"]',
      onHighlightStarted: () => {
        document.body.classList.add("driver-fc-dialog-step");
      },
      onDeselected: () => {
        // Removemos la clase antes de los pasos in-dialog para que driver
        // vuelva a su z default (~10000) y pueda resaltar los campos dentro del diálogo.
        document.body.classList.remove("driver-fc-dialog-step");
      },
      popover: {
        title: "Agregar una maquinaria",
        description:
          "Acá agregas una maquinaria al inventario de tu empresa. Haz clic en «Siguiente» para abrir el formulario.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          // Abre el diálogo y desactiva el focus trap para que driver mantenga
          // el control. El poll rAF espera a que monte el primer campo antes
          // de avanzar, evitando que driver intente resaltar un nodo inexistente.
          setCreateOpen(true);
          setGuideInDialog(true);

          cancelAnimationFrame(dialogPollRafRef.current);
          const deadline = performance.now() + 3000;
          const tick = () => {
            // Se espera al PRIMER campo que el paso siguiente va a resaltar —
            // el tipo, que en el drawer va arriba de todo.
            if (document.querySelector('[data-tour="maquinaria-tipo"]')) {
              driverObj.moveNext();
              return;
            }
            if (performance.now() >= deadline) {
              // Fallback: avanzamos igual para no dejar la guía atascada.
              driverObj.moveNext();
              return;
            }
            dialogPollRafRef.current = requestAnimationFrame(tick);
          };
          dialogPollRafRef.current = requestAnimationFrame(tick);
        },
      },
    },
    // Paso 3 — Tipo de maquinaria (dentro del diálogo, primer grupo visible)
    {
      element: '[data-tour="maquinaria-tipo"]',
      popover: {
        title: "Tipo de maquinaria",
        description:
          "Empieza por el tipo: elige uno de los atajos (Excavadora, Grúa horquilla, Compactadora…) o escríbelo tú. Al elegirlo te proponemos un código interno libre, que igual puedes cambiar.",
      },
    },
    // Paso 4 — Campos Nombre / Código (dentro del diálogo)
    {
      element: '[data-tour="maquinaria-nombre"]',
      popover: {
        title: "Nombre y código interno",
        description:
          "Ingresa el nombre con el que la cuadrilla la llama en terreno, por ejemplo «Excavadora hidráulica». El código interno (opcional) te permite identificarla con tu nomenclatura propia, por ejemplo «EXC-001».",
      },
    },
    // Paso 5 — Campos Marca / Modelo (dentro del diálogo)
    {
      element: '[data-tour="maquinaria-marca-modelo"]',
      popover: {
        title: "Marca y modelo",
        description:
          "Completa la marca y el modelo. Estos datos aparecen en los checks de maquinaria y en los reportes de equipos.",
      },
    },
    // Paso 6 — Botón Guardar (dentro del diálogo)
    {
      element: '[data-tour="maquinaria-guardar"]',
      popover: {
        title: "Guardar maquinaria",
        description:
          "Cuando termines de completar los datos, guarda para registrar la maquinaria en tu inventario.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          // Al salir de los pasos in-dialog, volvemos al comportamiento modal normal.
          setGuideInDialog(false);
          driverObj.moveNext();
        },
      },
    },
    // Paso 7 — Popover de cierre/confirmación (sin elemento anclado)
    {
      popover: {
        title: "¡Listo!",
        description:
          "Puedes reabrir esta guía cuando quieras haciendo clic en «Ver guía» en el panel de primeros pasos, o en el botón de ayuda de esta página.",
      },
    },
  ], [setCreateOpen, setGuideInDialog]);

  const { startGuide } = useGuide("guide-maquinaria", { buildSteps: buildMaquinariaGuideSteps });
  useRegisterPageGuide(startGuide);
  usePendingGuide("guide-maquinaria", startGuide, { anchorSelector: '[data-tour="maquinaria-crear"]' });
  useGuideAutostart("guide-maquinaria", startGuide, { anchorSelector: '[data-tour="maquinaria-crear"]' });

  // Limpieza del rAF de polling al desmontar
  useEffect(() => {
    return () => cancelAnimationFrame(dialogPollRafRef.current);
  }, []);
  // --- fin Guía de Maquinaria ---

  /**
   * Inventario y papelera se piden JUNTOS, no al cambiar de pestaña: los
   * contadores del riel segmentado tienen que estar bien desde el primer
   * pintado, y con dos listados de este tamaño el costo es una request más.
   */
  const cargar = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [inv, pap] = await Promise.all([
        api.get<MaquinariasResponse>("/maquinarias?all=1&include_inactivos=1"),
        api.get<MaquinariasResponse>("/maquinarias?all=1&en_papelera=1"),
      ]);
      setInventario(inv.data);
      setPapelera(pap.data);
      setTiposTotal(inv.documentos?.tipos_total ?? 0);
    } catch {
      setLoadError("No se pudo cargar la lista de maquinarias.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // ── Mover a papelera (DELETE → soft delete) ───────────────────────────────
  const moverAPapelera = async () => {
    if (!aEliminar) return;
    setEliminando(true);
    try {
      await api.delete(`/maquinarias/${aEliminar.id}`);
      toast.success(`"${aEliminar.nombre}" movida a la papelera. Puedes restaurarla cuando quieras.`);
      setAEliminar(null);
      cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Esta maquinaria ya está en la papelera.");
      } else {
        toast.error("No se pudo mover la maquinaria a la papelera.");
      }
    } finally {
      setEliminando(false);
    }
  };

  // ── Restaurar desde papelera ──────────────────────────────────────────────
  const restaurar = async () => {
    if (!aRestaurar) return;
    setRestaurando(true);
    try {
      await api.post(`/maquinarias/${aRestaurar.id}/restaurar`, {});
      toast.success(`"${aRestaurar.nombre}" restaurada.`);
      setARestaurar(null);
      cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Esta maquinaria no está en la papelera.");
      } else {
        toast.error("No se pudo restaurar la maquinaria.");
      }
    } finally {
      setRestaurando(false);
    }
  };

  // ── Purgar (hard delete, irreversible) ────────────────────────────────────
  const purgar = async () => {
    if (!aPurgar) return;
    setPurgando(true);
    try {
      await api.delete(`/maquinarias/${aPurgar.id}/purgar`);
      toast.success(`"${aPurgar.nombre}" eliminada permanentemente.`);
      setAPurgar(null);
      setConfirmPurgarTexto("");
      cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Solo se puede purgar una maquinaria desde la papelera.");
      } else {
        toast.error("No se pudo purgar la maquinaria.");
      }
      setAPurgar(null);
      setConfirmPurgarTexto("");
    } finally {
      setPurgando(false);
    }
  };

  // ── Derivados ─────────────────────────────────────────────────────────────

  const activos = useMemo(() => inventario.filter((m) => m.activo), [inventario]);
  const inactivos = useMemo(() => inventario.filter((m) => !m.activo), [inventario]);

  /**
   * Códigos y series ya tomados — inventario Y papelera. `maquinarias` usa
   * SoftDeletes y la regla `unique` de `MaquinariaRequest` consulta la tabla
   * cruda, así que una máquina en la papelera sigue ocupando su código:
   * sugerir uno que ella tenga devolvería un 422 desconcertante.
   */
  const codigosUsados = useMemo(
    () => [...inventario, ...papelera].map((m) => m.codigo ?? "").filter(Boolean),
    [inventario, papelera],
  );
  const seriesUsadas = useMemo(
    () => [...inventario, ...papelera].map((m) => m.serie ?? "").filter(Boolean),
    [inventario, papelera],
  );

  /**
   * Las cuatro tarjetas se pliegan del MISMO conjunto (`inventario`) y con la
   * MISMA función que pinta la columna. Una maquinaria inactiva con documentos
   * vencidos sigue contando: sale de las asignaciones operativas, no del
   * inventario, y esos papeles hay que arreglarlos antes de reactivarla.
   */
  const kpis = useMemo(() => {
    const estados = inventario.map((m) => estadoDocumental(m.documentos, tiposTotal));
    return {
      total: inventario.length,
      conVencidos: estados.filter((e) => e && e.vencidos > 0).length,
      conFaltantes: estados.filter((e) => e && e.faltantes > 0).length,
      completos: estados.filter((e) => e?.completo).length,
    };
  }, [inventario, tiposTotal]);

  const filas = useMemo(() => {
    const base =
      vista === "todos"
        ? inventario
        : vista === "activos"
          ? activos
          : vista === "inactivos"
            ? inactivos
            : papelera;
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter(
      (m) =>
        m.nombre.toLowerCase().includes(q) ||
        (m.codigo ?? "").toLowerCase().includes(q) ||
        (m.tipo ?? "").toLowerCase().includes(q) ||
        (m.marca ?? "").toLowerCase().includes(q) ||
        (m.modelo ?? "").toLowerCase().includes(q) ||
        (m.serie ?? "").toLowerCase().includes(q),
    );
  }, [vista, inventario, activos, inactivos, papelera, search]);

  const esPapelera = vista === "papelera";

  const mensajeVacio = search.trim()
    ? "Ninguna maquinaria coincide con esa búsqueda."
    : esPapelera
      ? "No hay maquinaria en la papelera."
      : vista === "inactivos"
        ? "No hay maquinaria inactiva."
        : // Con inventario cargado pero nada activo, el mensaje de bienvenida
          // mentiría: sí hay maquinaria, está toda inactiva.
          vista === "activos" && inventario.length > 0
          ? "No hay maquinaria activa."
          : 'Todavía no hay maquinaria. Haz clic en "Nueva maquinaria" para empezar.';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Maquinaria"
        subtitle="Tu maquinaria, con el estado de la documentación de cada equipo."
        rightContent={
          <Button
            data-tour="maquinaria-crear"
            onClick={() => setCreateOpen(true)}
            className="shrink-0"
          >
            <Plus className="h-4 w-4" />
            Nueva maquinaria
          </Button>
        }
      />

      {/*
        modal={false} desactiva el focus trap de Radix Dialog durante los pasos
        in-dialog de la guía. Esto permite que los botones Siguiente/Anterior de
        driver.js (renderizados fuera del DOM del diálogo) sean clickeables.
        En el flujo normal sin guía, guideInDialog=false y el diálogo es modal.
      */}
      <FormDrawer
        open={createOpen}
        onOpenChange={(open) => {
          if (!open) setGuideInDialog(false);
          setCreateOpen(open);
        }}
        side="right"
        modal={guideInDialog ? false : undefined}
        dismissible={guideInDialog ? false : undefined}
      >
        <MaquinariaFormDrawer
          key={createOpen ? "create-open" : "create-closed"}
          mode="create"
          codigosUsados={codigosUsados}
          seriesUsadas={seriesUsadas}
          documentosTotal={tiposTotal}
          onSaved={() => {
            setCreateOpen(false);
            setGuideInDialog(false);
            cargar();
            invalidate();
          }}
        />
      </FormDrawer>

      <KpiGrid min={196}>
        <KpiCard
          label="Total en inventario"
          valor={kpis.total}
          detalle={`${plural(activos.length, "activa", "activas")} · ${plural(
            inactivos.length,
            "inactiva",
            "inactivas",
          )}`}
        />
        <KpiCard
          label="Con documentos vencidos"
          valor={kpis.conVencidos}
          alerta={kpis.conVencidos > 0}
          detalle={`de ${plural(kpis.total, "maquinaria", "maquinarias")} en el inventario`}
        />
        <KpiCard
          label="Con documentos sin subir"
          valor={kpis.conFaltantes}
          detalle={
            tiposTotal === 0
              ? "sin catálogo de documentos configurado"
              : `les falta al menos 1 de ${plural(tiposTotal, "documento", "documentos")}`
          }
        />
        <KpiCard
          label="Documentación completa"
          valor={`${kpis.completos}/${kpis.total}`}
          detalle="con todo el catálogo cargado y nada vencido"
        />
      </KpiGrid>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 pt-6">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </CardContent>
        </Card>
      ) : loadError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 pt-6 text-center">
            <AlertCircle className="text-destructive h-6 w-6" />
            <p className="text-muted-foreground text-sm">{loadError}</p>
            <button type="button" onClick={cargar} className="text-primary-hover text-sm underline">
              Reintentar
            </button>
          </CardContent>
        </Card>
      ) : (
        <DataTable
          rows={filas}
          getRowKey={(m) => m.id}
          // 1002px de columnas (96+170+120+150+126+168+100+72) + 28px del
          // `px-3.5` de la fila. Por debajo de esa suma la grilla se desborda de
          // su contenedor en vez de scrollear, y la cabecera deja de alinearse
          // con las filas.
          minWidth={1030}
          pageSize={20}
          onRowClick={esPapelera ? undefined : (m) => navigate(`/maquinaria/${m.id}/ficha`)}
          emptyMessage={mensajeVacio}
          emptyIcon={esPapelera ? "delete" : "precision_manufacturing"}
          toolbar={
            <>
              <ToggleGroup
                type="single"
                variant="segmented"
                value={vista}
                onValueChange={(v) => {
                  if (!v) return;
                  setVista(v as Vista);
                  setSearch("");
                }}
              >
                <ToggleGroupItem value="todos">
                  Todas
                  <ToggleGroupCount>{inventario.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="activos">
                  Activas
                  <ToggleGroupCount>{activos.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="inactivos">
                  Inactivas
                  <ToggleGroupCount>{inactivos.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="papelera">
                  <Icon name="delete" size={16} />
                  Papelera
                  <ToggleGroupCount>{papelera.length}</ToggleGroupCount>
                </ToggleGroupItem>
              </ToggleGroup>

              <div className="relative min-w-[190px] flex-1">
                <Search className="text-soft absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder="Buscar por código, nombre, tipo o marca…"
                  className="h-9 pl-8 text-[13px]"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </>
          }
          columns={[
            {
              key: "codigo",
              header: "Código",
              width: "96px",
              cell: (m) => (
                <span className="fc-num text-soft pr-2 text-[11.5px]">{m.codigo ?? "—"}</span>
              ),
            },
            {
              key: "nombre",
              header: "Nombre",
              width: "minmax(170px,1.5fr)",
              cell: (m) => <CeldaNombre m={m} />,
            },
            {
              key: "tipo",
              header: "Tipo",
              width: "120px",
              cell: (m) => (
                <TextoTruncado className="block truncate pr-2 text-[12.5px]">
                  {m.tipo ?? "—"}
                </TextoTruncado>
              ),
            },
            {
              key: "marca",
              header: "Marca / modelo",
              width: "minmax(150px,1fr)",
              cell: (m) => {
                const marca = [m.marca, m.modelo].filter(Boolean).join(" ");
                return (
                  <TextoTruncado className="block truncate pr-2 text-[12.5px]">
                    {marca ? (m.anio ? `${marca} · ${m.anio}` : marca) : (m.anio ?? "—")}
                  </TextoTruncado>
                );
              },
            },
            {
              key: "serie",
              header: "Serie",
              width: "126px",
              cell: (m) => (
                <TextoTruncado className="fc-num text-soft block truncate pr-2 text-[11px]">
                  {m.serie ?? "—"}
                </TextoTruncado>
              ),
            },
            {
              key: "documentos",
              header: "Documentos",
              width: "168px",
              cell: (m) =>
                // En la papelera la columna no aplica: la maquinaria salió del
                // inventario y sus documentos no se están exigiendo.
                esPapelera ? (
                  <span className="text-soft text-xs">—</span>
                ) : (
                  <CeldaDocumentosRecurso resumen={m.documentos} tiposTotal={tiposTotal} />
                ),
            },
            {
              key: "estado",
              header: "Estado",
              width: "100px",
              cell: (m) => <EstadoBadge m={m} />,
            },
            {
              // Sin encabezado, igual que el prototipo. Cadena vacía y NO
              // `srOnlyHeader`: `sr-only` es `position:absolute`, así que sobre
              // una celda de la grilla la saca del flujo y corre las columnas
              // siguientes — ver el comentario en DataTable.
              key: "acciones",
              header: "",
              width: "72px",
              align: "right",
              cell: (m) =>
                esPapelera ? (
                  <div className="flex items-center justify-end gap-0.5">
                    <Tip texto="Devolver al inventario">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Restaurar ${m.nombre}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setARestaurar(m);
                        }}
                      >
                        <Icon name="restore_from_trash" size={16} />
                      </Button>
                    </Tip>
                    <Tip texto="Eliminar definitivamente">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-soft hover:text-destructive"
                        aria-label={`Eliminar definitivamente ${m.nombre}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAPurgar(m);
                          setConfirmPurgarTexto("");
                        }}
                      >
                        <Icon name="delete_forever" size={16} />
                      </Button>
                    </Tip>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-0.5">
                    <Tip texto="Abrir ficha">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Abrir ficha de ${m.nombre}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/maquinaria/${m.id}/ficha`);
                        }}
                      >
                        <Icon name="chevron_right" size={17} />
                      </Button>
                    </Tip>
                    <Tip texto="Mover a la papelera">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-soft hover:text-destructive"
                        aria-label={`Mover ${m.nombre} a la papelera`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAEliminar(m);
                        }}
                      >
                        {/* `delete`, no `delete_outline`: en Material Symbols el
                            relleno es un EJE (FILL), no un nombre distinto, y la
                            app carga la fuente con FILL@0 — así que `delete` ya
                            es la variante de contorno del prototipo. Un nombre
                            inexistente se dibujaría como su propio texto. */}
                        <Icon name="delete" size={16} />
                      </Button>
                    </Tip>
                  </div>
                ),
            },
          ]}
        />
      )}

      {/* Confirmar: mover a papelera */}
      <AlertDialog open={aEliminar !== null} onOpenChange={(open) => !open && setAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mover a papelera</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {aEliminar ? (
                  <div className="space-y-2">
                    <p>
                      ¿Quieres mover <strong>{aEliminar.nombre}</strong> a la papelera?
                    </p>
                    <p>
                      Va a desaparecer del inventario pero puedes{" "}
                      <strong>restaurarla en cualquier momento</strong> desde la pestaña Papelera.
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Si sólo quieres sacarla de las asignaciones operativas sin quitarla de la
                      empresa, desactívala desde su ficha.
                    </p>
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                moverAPapelera();
              }}
              disabled={eliminando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mover a papelera"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar: restaurar */}
      <AlertDialog open={aRestaurar !== null} onOpenChange={(open) => !open && setARestaurar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar maquinaria</AlertDialogTitle>
            <AlertDialogDescription>
              {aRestaurar ? (
                <>
                  ¿Restaurar <strong>{aRestaurar.nombre}</strong>? Vuelve al inventario con el
                  estado que tenía antes de ser eliminada.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restaurando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                restaurar();
              }}
              disabled={restaurando}
            >
              {restaurando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Restaurar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar: purgar (typed-confirm) */}
      <AlertDialog
        open={aPurgar !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAPurgar(null);
            setConfirmPurgarTexto("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Eliminar permanentemente</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {aPurgar ? (
                  <div className="space-y-3">
                    <p>
                      Estás por eliminar definitivamente <strong>{aPurgar.nombre}</strong>. Esta
                      acción es <strong>irreversible</strong>: se borrarán todos sus datos del
                      sistema, incluidos sus documentos.
                    </p>
                    <p className="font-medium">
                      Escribe <span className="fc-num text-destructive">ELIMINAR</span> para
                      confirmar:
                    </p>
                    <Input
                      value={confirmPurgarTexto}
                      onChange={(e) => setConfirmPurgarTexto(e.target.value)}
                      placeholder="ELIMINAR"
                      className="fc-num"
                      disabled={purgando}
                      autoFocus
                    />
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={purgando}
              onClick={() => {
                setAPurgar(null);
                setConfirmPurgarTexto("");
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                purgar();
              }}
              disabled={purgando || confirmPurgarTexto !== "ELIMINAR"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {purgando ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Eliminar permanentemente"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface MaquinariaFormDrawerProps {
  mode: "create" | "edit";
  maquinaria?: MaquinariaType;
  onSaved: () => void;
  codigosUsados: string[];
  seriesUsadas: string[];
  documentosTotal: number;
}

/**
 * Envoltorio del drawer de alta — el cuerpo de campos vive en `MaquinariaForm`
 * (compartido con el tab Información de la Ficha de maquinaria). La EDICIÓN de
 * una maquinaria existente vive sólo en la ficha: cambiar marca, áreas o el
 * estado activo sin ver antes sus documentos es justo la decisión que conviene
 * tomar con el contexto delante.
 */
function MaquinariaFormDrawer({
  mode,
  maquinaria,
  onSaved,
  codigosUsados,
  seriesUsadas,
  documentosTotal,
}: MaquinariaFormDrawerProps) {
  return (
    <FormDrawerContent>
      <FormDrawerHeader>
        <div className="flex items-center gap-3">
          <span className="bg-primary-50 text-primary-hover flex size-9 shrink-0 items-center justify-center rounded-[10px]">
            <Icon name="precision_manufacturing" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <FormDrawerTitle>
              {mode === "create" ? "Nueva maquinaria" : "Editar maquinaria"}
            </FormDrawerTitle>
            <FormDrawerDescription>
              {mode === "create"
                ? "Queda disponible para los checklists de terreno y las asignaciones."
                : "Actualiza los datos de la maquinaria."}
            </FormDrawerDescription>
          </div>
        </div>
      </FormDrawerHeader>

      <MaquinariaForm
        mode={mode}
        maquinaria={maquinaria}
        onSaved={onSaved}
        codigosUsados={codigosUsados}
        seriesUsadas={seriesUsadas}
        documentosTotal={documentosTotal}
        formClassName={formDrawerBodyClass}
        renderFooter={({ isSubmitting, resumen }) => (
          <FormDrawerFooter>
            {/* La pista dice qué falta ANTES de intentar guardar, en vez de
                dejar el botón inerte sin explicar por qué. */}
            <FormDrawerFooterHint>{resumen}</FormDrawerFooterHint>
            <FormDrawerClose asChild>
              <Button type="button" variant="outline" disabled={isSubmitting}>
                Cancelar
              </Button>
            </FormDrawerClose>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="gap-2"
              data-tour="maquinaria-guardar"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {mode === "create" ? "Agregar al inventario" : "Guardar cambios"}
            </Button>
          </FormDrawerFooter>
        )}
      />
    </FormDrawerContent>
  );
}
